import LogoutIcon from '@mui/icons-material/Logout';
import { Button, DialogActions, DialogContentText } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import { useContext, useEffect, useMemo, useState } from 'react';
import { AccountContext } from '../context/account';
import { OrganisationContext } from '../context/organisation';
import { lastAdminModal, permissionsModal } from '../data/content';
import styles from "../styles/components/user-settings-modal.module.scss";
import { InfoModal } from './info-modal';
import { ModalTitle } from './modal-title';
import { OrganisationUsers } from './organisation-users';
import { LeaveOrganisationConfirm } from './user-settings-modal';

interface UserSettingsModalProps {
  open: boolean;
  handleClose: (forceClose: boolean) => void;
  id: number | string | null;
  onLeft?: () => void; // parent refetches the user's org list after a leave
}

export const OrganisationUsersModal = ({ open, handleClose, id, onLeft }: UserSettingsModalProps) => {
  const [showInfoModal, setShowInfoModal] = useState(false)
  const [confirmLeave, setConfirmLeave] = useState(false)
  const [showLastAdminNotice, setShowLastAdminNotice] = useState(false)

  const { organisationUsers } = useContext(OrganisationContext);
  const { account } = useContext(AccountContext);
  const { uid } = account;

  // Derived gate for the admin-leave action. organisationUsers is the same
  // context list OrganisationUsers renders, so promoting someone in that table
  // refetches the context and these values update live.
  const { iAmAdmin, otherAdminsExist, iAmMember } = useMemo(() => {
    const users = Array.isArray(organisationUsers) ? organisationUsers : [];
    const mine = users.find((u: any) => u.user_id === uid);
    const admins = users.filter((u: any) => u.role === 'admin');
    return {
      iAmMember: !!mine,
      iAmAdmin: mine?.role === 'admin',
      // "another admin remains after I go"
      otherAdminsExist: admins.some((u: any) => u.user_id !== uid),
    };
  }, [organisationUsers, uid]);

  // Only an admin who could otherwise be stranded needs the blocking message.
  const blockedAsLastAdmin = iAmMember && iAmAdmin && !otherAdminsExist;

  const orgName = useMemo(() => {
    const users = Array.isArray(organisationUsers) ? organisationUsers : [];
    // organisation_users rows carry organisation_id; name may not be on them,
    // so fall back to a generic label if the list doesn't expose it.
    const anyRow: any = users[0];
    return anyRow?.organisation_name || anyRow?.name || 'this organisation';
  }, [organisationUsers]);

  // Once another admin exists (they promoted someone), retract the notice so it
  // doesn't linger after the blocker is resolved.
  useEffect(() => {
    if (otherAdminsExist && showLastAdminNotice) setShowLastAdminNotice(false)
  }, [otherAdminsExist, showLastAdminNotice])

  const onClose = () => {
    handleClose(false)
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
    >
      <ModalTitle onClose={onClose}>Manage users on your organisation</ModalTitle>
      <DialogContent
        sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}
      >
        <DialogContentText className={styles.row}>
          You can add/remove users for this organisation or change their permission.
          <Button
            variant="contained"
            onClick={()=> {
              setShowInfoModal(true)
            }}
          >
            Roles and User Permissions info
        </Button>
        </DialogContentText>

        <OrganisationUsers id={id} />
      </DialogContent>

      <DialogActions sx={{ pb: 3, px: 3, justifyContent: 'space-between' }}>
        <span>
          {iAmMember &&
            <>
              <Button
                color="error"
                variant="outlined"
                startIcon={<LogoutIcon />}
                onClick={() => {
                  if (blockedAsLastAdmin) {
                    // Don't call the API — explain why in a modal, on the attempt.
                    setShowLastAdminNotice(true)
                  } else {
                    setConfirmLeave(true)
                  }
                }}
              >
                Leave organisation
              </Button>
            </>
          }
        </span>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>

      <InfoModal
        open={showInfoModal}
        title={permissionsModal.title}
        content={permissionsModal.content}
        maxWidth="sm"
        onClose={() => {
          setShowInfoModal(false)
        }}
      />

      {confirmLeave &&
        <LeaveOrganisationConfirm
          org={id != null ? { id, name: orgName } : null}
          onClose={() => setConfirmLeave(false)}
          onLeft={() => {
            setConfirmLeave(false)
            onLeft?.()   // parent refetches the user's org list
            handleClose(true) // close manage-users; the admin is no longer in this org
          }}
        />
      }

      <InfoModal
        open={showLastAdminNotice}
        title={lastAdminModal.title}
        content={lastAdminModal.content}
        maxWidth="xs"
        onClose={() => {
          setShowLastAdminNotice(false)
        }}
      />
    </Dialog>
  );
}